import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { isValidUUID } from '@/lib/utils';
import type { UserChecklistItem, ItemStatus, ChecklistFilters } from '../types';

interface UseChecklistItemsOptions {
  checklistId: string;
}

export function useChecklistItems({ checklistId }: UseChecklistItemsOptions) {
  const { user } = useAuth();
  const [items, setItems] = useState<UserChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ChecklistFilters>({ status: 'all' });

  // Validate checklistId to prevent 400 errors
  const validChecklistId = isValidUUID(checklistId) ? checklistId : null;

  // Fetch items
  const fetchItems = useCallback(async () => {
    if (!validChecklistId) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('user_checklist_items')
        .select('*')
        .eq('checklist_id', validChecklistId)
        .order('order_index', { ascending: true });

      if (fetchError) throw fetchError;

      setItems(data || []);
    } catch (err) {
      console.error('Error fetching checklist items:', err);
      setError('Fehler beim Laden der Items');
    } finally {
      setIsLoading(false);
    }
  }, [validChecklistId]);

  // Toggle item status (open <-> completed)
  const toggleItem = useCallback(async (itemId: string, currentStatus: ItemStatus) => {
    if (!user?.id) return false;

    const newStatus: ItemStatus = currentStatus === 'completed' ? 'open' : 'completed';

    // Optimistic update
    setItems(prev => prev.map(item =>
      item.id === itemId
        ? {
            ...item,
            status: newStatus,
            completed_by: newStatus === 'completed' ? user.id : null,
            completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
          }
        : item
    ));

    try {
      const updateData: Partial<UserChecklistItem> = {
        status: newStatus,
        completed_by: newStatus === 'completed' ? user.id : null,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('user_checklist_items')
        .update(updateData)
        .eq('id', itemId);

      if (updateError) throw updateError;

      return true;
    } catch (err) {
      console.error('Error toggling item:', err);
      // Rollback on error
      await fetchItems();
      return false;
    }
  }, [user?.id, fetchItems]);

  // Mark item as not needed
  const markNotNeeded = useCallback(async (itemId: string) => {
    try {
      const { error: updateError } = await supabase
        .from('user_checklist_items')
        .update({
          status: 'not_needed',
          updated_at: new Date().toISOString(),
        })
        .eq('id', itemId);

      if (updateError) throw updateError;

      await fetchItems();
      return true;
    } catch (err) {
      console.error('Error marking item as not needed:', err);
      return false;
    }
  }, [fetchItems]);

  // Update item notes with tracking who edited
  const updateNotes = useCallback(async (itemId: string, notes: string) => {
    if (!user?.id) return false;

    const now = new Date().toISOString();

    try {
      const { error: updateError } = await supabase
        .from('user_checklist_items')
        .update({
          notes: notes || null,
          notes_updated_by: user.id,
          notes_updated_at: now,
          updated_at: now,
        })
        .eq('id', itemId);

      if (updateError) throw updateError;

      setItems(prev => prev.map(item =>
        item.id === itemId
          ? {
              ...item,
              notes: notes || null,
              notes_updated_by: user.id,
              notes_updated_at: now,
            }
          : item
      ));

      return true;
    } catch (err) {
      console.error('Error updating notes:', err);
      return false;
    }
  }, [user?.id]);

  // Add custom item
  const addItem = useCallback(async (title: string) => {
    if (!validChecklistId) return null;

    try {
      const maxOrder = Math.max(0, ...items.map(i => i.order_index || 0));

      const { data, error: insertError } = await supabase
        .from('user_checklist_items')
        .insert({
          checklist_id: validChecklistId,
          title,
          status: 'open',
          order_index: maxOrder + 1,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await fetchItems();
      return data;
    } catch (err) {
      console.error('Error adding item:', err);
      setError('Fehler beim Hinzufügen');
      return null;
    }
  }, [validChecklistId, items, fetchItems]);

  // Delete custom item
  const deleteItem = useCallback(async (itemId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('user_checklist_items')
        .delete()
        .eq('id', itemId);

      if (deleteError) throw deleteError;

      setItems(prev => prev.filter(item => item.id !== itemId));
      return true;
    } catch (err) {
      console.error('Error deleting item:', err);
      return false;
    }
  }, []);

  // Filter items
  const filteredItems = items.filter(item => {
    if (filters.status === 'all') return true;
    return item.status === filters.status;
  });

  // Calculate progress
  const progress = {
    total: items.length,
    completed: items.filter(i => i.status === 'completed').length,
    notNeeded: items.filter(i => i.status === 'not_needed').length,
    open: items.filter(i => i.status === 'open').length,
    percentage: items.length > 0
      ? Math.round((items.filter(i => i.status === 'completed').length / items.length) * 100)
      : 0,
  };

  // Initial fetch
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Setup realtime subscription
  useEffect(() => {
    if (!validChecklistId) return;

    const channel = supabase
      .channel(`checklist-items-${validChecklistId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_checklist_items',
          filter: `checklist_id=eq.${validChecklistId}`,
        },
        () => {
          fetchItems();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [validChecklistId, fetchItems]);

  return {
    items: filteredItems,
    allItems: items,
    isLoading,
    error,
    progress,
    filters,
    setFilters,
    toggleItem,
    markNotNeeded,
    updateNotes,
    addItem,
    deleteItem,
    refetch: fetchItems,
  };
}
